import { useQueries } from '@tanstack/react-query';

import { postConvertedCurrency } from '~/services/fxService';

const AUD = 'AUD';

/** svc-fx does not support LAK, so tour costing treats LAK amounts as VND. */
export const FX_CURRENCY_OVERRIDES: Record<string, string> = { LAK: 'VND' };

export const fxConversionQueryKeys = {
  all: ['fx-conversions'] as const,
  audBaseRate: (currency: string) => [...fxConversionQueryKeys.all, 'aud-base-rate', currency] as const,
};

export const getFxCurrency = (currency: string): string => FX_CURRENCY_OVERRIDES[currency] ?? currency;

/**
 * Reads one AUD base rate per source currency. Supplier amounts become AUD by
 * applying the inverse rate, avoiding an svc-fx request for every price point.
 */
export function useGetAudExchangeRates(currencies: Array<string>) {
  const sourceCurrencies = Array.from(new Set(currencies.filter((currency) => currency !== AUD))).sort();
  const targetCurrencies = Array.from(new Set(sourceCurrencies.map(getFxCurrency))).sort();

  return useQueries({
    queries: targetCurrencies.map((currency) => ({
      queryKey: fxConversionQueryKeys.audBaseRate(currency),
      queryFn: async () => {
        const response = await postConvertedCurrency({
          total: 1,
          baseCurrencyCode: AUD,
          targetCurrencyCode: currency,
        });

        return response.result.convertedTotal;
      },
      staleTime: 5 * 60 * 1000,
    })),
    combine: (results) => {
      const ratesByTargetCurrency = Object.fromEntries(
        targetCurrencies.map((currency, index) => [currency, results[index]?.data]),
      ) as Record<string, number | undefined>;
      const isLoadingByTargetCurrency = Object.fromEntries(
        targetCurrencies.map((currency, index) => [currency, results[index]?.isLoading ?? false]),
      ) as Record<string, boolean>;

      return {
        audToSourceCurrencyRate: {
          [AUD]: 1,
          ...Object.fromEntries(
            sourceCurrencies.map((currency) => [currency, ratesByTargetCurrency[getFxCurrency(currency)]]),
          ),
        } as Record<string, number | undefined>,
        isLoadingByCurrency: Object.fromEntries(
          sourceCurrencies.map((currency) => [currency, isLoadingByTargetCurrency[getFxCurrency(currency)] ?? false]),
        ) as Record<string, boolean>,
      };
    },
  });
}

export const convertAmountToAud = (
  amount: number,
  currency: string,
  audToSourceCurrencyRate: Record<string, number | undefined>,
): number | undefined => {
  const rate = audToSourceCurrencyRate[currency];

  return rate === undefined || rate <= 0 ? undefined : amount * (1 / rate);
};

export interface CostPriceConversion {
  serviceId: string;
  amount: number | null;
  currency: string | null;
}

/** Converts each supplier service's departure total into the common AUD reporting currency. */
export function useGetConvertedCostPricesInAud(costPrices: Array<CostPriceConversion>) {
  const currencies = costPrices.flatMap((costPrice) => (costPrice.currency === null ? [] : [costPrice.currency]));
  const { audToSourceCurrencyRate, isLoadingByCurrency } = useGetAudExchangeRates(currencies);

  return {
    convertedCostsByService: Object.fromEntries(
      costPrices.map((costPrice) => [
        costPrice.serviceId,
        costPrice.amount === null || costPrice.currency === null
          ? undefined
          : convertAmountToAud(costPrice.amount, costPrice.currency, audToSourceCurrencyRate),
      ]),
    ) as Record<string, number | undefined>,
    isLoadingByService: Object.fromEntries(
      costPrices.map((costPrice) => [
        costPrice.serviceId,
        costPrice.currency === null ? false : (isLoadingByCurrency[costPrice.currency] ?? false),
      ]),
    ) as Record<string, boolean>,
  };
}
